import {
  GetMessagesResponseItem,
  MessageControllerService
} from "@9amhealth/openapi";
import { App } from "@capacitor/app";
import { DateFormatter, parseAbsoluteToLocal } from "@internationalized/date";
import { Cubit } from "blac-next";
import type { VirtuosoHandle } from "react-virtuoso";
import appErrors from "src/constants/appErrors";
import { globalEvents } from "src/constants/globalEvents";
import { FileUploadMegabyteLimit } from "src/constants/limits";
import { logSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import {
  CHAT_PAGINATION,
  DEAD_LETTER_QUEUE,
  TIMEOUTS,
  VIRTUAL_SCROLL
} from "./constants/ChatConstants";
import type { ChatBlocDependencies } from "./types/ChatBlocDependencies";
import { createDefaultChatBlocDependencies } from "./types/ChatBlocDependencies";
import { FeatureFlagName, featureFlags } from "src/lib/featureFlags";
import { getSupportedUserLocale } from "src/lib/i18next";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import type { ObserverCallback } from "src/state/WebSocketBloc/WebSocketBloc";
import {
  MESSAGE_METADATA_KEYS,
  WebsocketMessageType
} from "src/state/WebSocketBloc/WebSocketBloc";
import { appViewState, pushNotificationState } from "src/state/state";
import type { TranslationKey } from "src/types/translationKey";
import * as SendingMessagesTracker from "./SendingMessagesTracker";
import * as FileMetadataUtils from "./utils/FileMetadataUtils";
import { MessageTransport } from "./services/MessageTransport";
import { MessageQueue } from "./services/MessageQueue";
import { MessageRepository } from "./services/MessageRepository";
import { PollingService } from "./services/PollingService";
import { MessageStateManager } from "./services/MessageStateManager";
import { WS_STATE } from "src/constants/websocket";

export enum MessageMetaField {
  name = "file.<id>.name",
  type = "file.<id>.type",
  size = "file.<id>.size",
  source = "file.<id>.source",
  imageWidth = "file.<id>.image.width",
  imageHeight = "file.<id>.image.height"
}

export interface MessageFileMetadata {
  /** Original filename */
  name: string;
  /** MIME type of the file (e.g., "image/png", "application/pdf") */
  type: string;
  /** File size in bytes (stored as string) */
  size: string;
  /** File source identifier or URL for retrieval */
  source: string;
  /** Width in pixels for image files (stored as string) */
  imageWidth: string;
  /** Height in pixels for image files (stored as string) */
  imageHeight: string;
}

export type ChatMessageType = "text/html" | "text/plain";

/**
 * Special message types that have specific UI rendering or behavior.
 * These messages may display custom components or trigger specific actions.
 */
export enum ChatMessageMetaType {
  /** Message contains a lab report file upload */
  labReportUpload = "lab-report-upload",
  /** Message displays lab values/results in a structured format */
  labValueDisplay = "lab-value-display",
  /** Message contains NPS (Net Promoter Score) feedback survey */
  npsFeedback = "nps-feedback",
  /** Message contains CSAT (Customer Satisfaction) feedback survey */
  csatFeedback = "csat-feedback"
}
/**
 * Type of sender for a chat message, determining how the message is displayed.
 */
export type KnownSenderType =
  | "STAFF_CUSTOMER_SUPPORT"
  | "STAFF_DIABETES_EDUCATOR"
  | "STAFF_PHYSICIAN"
  | "STAFF"
  | "USER"
  | undefined;

export interface ChatMessageComputedData {
  /** Reference ID for local messages before they're confirmed by the server */
  localRefId?: string;
  /** True if this message is still local (not yet confirmed by server) */
  isLocal: boolean;
  /** True if the message was sent by the current user */
  sentByUser: boolean;
  /** Whether to display the content value (may be hidden for certain message types) */
  showContentValue?: boolean;
  /** Display name of the message sender */
  senderName?: string;
  /** Job title or role description of the sender (e.g., "Physician", "Care Coach") */
  senderDescription?: string;
  /** Formatted date string for when the message was sent */
  messageSentDate?: string;
  /** True if this is a lab value display message */
  isLabValueDisplay?: boolean;
  /** True if this is a lab report upload message */
  isLabReportUpload?: boolean;
  /** True if this is an NPS feedback survey message */
  isNpsFeedback?: boolean;
  /** Array of file IDs attached to this message */
  filesIdList?: string[];
  /** Parsed file attachment metadata for rendering */
  fileAttachments: MessageFileMetadata[];
  /** IDs of lifeline items (medical data points) attached to this message */
  attachedLifelineItems: string[];
  /** True if the content was AI-generated rather than directly authored */
  contentValueGenerated?: boolean;
}

export interface ChatMessage {
  /** Placeholder type for virtual scrolling (used for spacing/loading states) */
  placeholder?: "bottom" | "top-small" | "top";
  /** Unique message ID (local IDs start with "local_") */
  id: string;
  /** Raw API response item (undefined for local messages not yet sent) */
  apiItem?: GetMessagesResponseItem;
  /** Computed/derived data for UI rendering */
  computed: ChatMessageComputedData;
  /** Message text content (may be HTML or plain text) */
  text?: string;
  /** ISO 8601 timestamp of when the message was received/sent */
  timestamp: string;
  /** Content type of the message */
  type?: ChatMessageType;
  /** Role/type of the message sender */
  senderType?: KnownSenderType;
  /** Special message type for custom rendering */
  metaType?: ChatMessageMetaType;
  /** True if the content was AI-generated */
  contentValueGenerated?: boolean;
}

enum ChatErrorMessages {
  /** File upload operation failed */
  fileUploadFailed = "fileUploadFailed",
  /** WebSocket connection is not established */
  notConnected = "error_websocket_not_connected"
}

export interface ChatError {
  /** Translation key for the error message */
  message: ChatErrorMessages;
  /** Optional additional context text for the error */
  text?: string;
  /** If true, the error blocks further actions until resolved */
  blocking: boolean;
}

export interface ChatViewState {
  /** Array of messages to display (includes both local and server messages) */
  messages: ChatMessage[];
  /** True when loading messages from the server */
  loading: boolean;
  /** Index of the first item in the virtual scroll list (for pagination) */
  firstItemIndex: number;
  /** True when new messages are available but not yet scrolled to */
  newMessageAvailable?: boolean;
  /** Array of current errors to display to the user */
  errors: ChatError[];
  /** Count of unread messages in the chat */
  unreadMessages?: number;
}

// Constants moved to ./constants/ChatConstants.ts
const KEY_FIRST_ITEM_INDEX = CHAT_PAGINATION.KEY_FIRST_ITEM_INDEX;

export class ChatBloc extends Cubit<ChatViewState> {
  verbose = false;
  userId: string = "";
  allMessages: ChatMessage[] = [];
  virtualListElement: VirtuosoHandle | null = null;
  scrollEndState = true;

  get moreHistoryAvailable(): boolean {
    return this.messageRepository.hasMoreHistory();
  }
  chatOpen = false;
  isClosed = false;

  /**
   * External dependencies (storage, WebSocket, auth, etc.)
   * Can be injected for testing or defaults to global singletons
   */
  private deps: ChatBlocDependencies;

  /**
   * Message transport layer - handles WebSocket/HTTP message sending with fallback
   */
  private messageTransport: MessageTransport;

  /**
   * Message queue - manages local messages with retry logic
   */
  private messageQueue: MessageQueue;

  /**
   * Message repository - handles message fetching, caching, and pagination
   */
  private messageRepository: MessageRepository;

  /**
   * Polling service - manages background polling for messages and unread counts
   */
  private pollingService: PollingService;

  /**
   * Message state manager - orchestrates message merging, deduplication, and state updates
   */
  private messageStateManager: MessageStateManager;

  /**
   * Cleanup handlers for event listeners to prevent memory leaks
   */
  private userClearHandler: () => void;
  private appStateListenerCleanup?: () => Promise<void>;

  /**
   * Tracks if the response time toast has been shown to avoid duplicates
   */
  private responseTimeToastShown = false;

  constructor(dependencies?: Partial<ChatBlocDependencies>) {
    super({
      messages: [],
      loading: false,
      firstItemIndex: KEY_FIRST_ITEM_INDEX,
      errors: []
    });

    // Initialize dependencies - use provided or default to global singletons
    this.deps = {
      ...createDefaultChatBlocDependencies(),
      ...dependencies
    };

    // Initialize message transport with dependencies
    this.messageTransport = new MessageTransport(
      this.deps,
      MessageControllerService
    );

    // Initialize message queue with dependencies
    this.messageQueue = new MessageQueue(this.deps);

    // Initialize message repository with dependencies
    this.messageRepository = new MessageRepository(this.deps);

    // Initialize polling service with dependencies and callbacks
    this.pollingService = new PollingService(this.deps, {
      onCheckUnreadMessages: this.checkUnreadMessages,
      onLoadFirstPageMessages: this.loadFirstPageMessages,
      onLoadNewerMessages: this.handlePollingLoadMessages,
      log: this.log
    });

    // Initialize message state manager
    this.messageStateManager = new MessageStateManager();

    // Create and register USER_CLEAR handler with cleanup tracking
    this.userClearHandler = () => {
      this.allMessages = [];

      // Clear message transport state
      this.messageTransport.clearSendingState();

      // Clear message queue
      this.messageQueue.clear();
      this.messageQueue.clearProcessingState();

      // Clear global sending tracker to prevent cross-user contamination
      SendingMessagesTracker.clearSentMessages();

      // Stop polling
      this.pollingService.stop();

      // Reset state to initial values
      this.emit({
        messages: [],
        loading: false,
        firstItemIndex: KEY_FIRST_ITEM_INDEX,
        errors: []
      });
    };
    window.addEventListener(globalEvents.USER_CLEAR, this.userClearHandler);

    // Start polling
    this.pollingService.start();
    void this.checkUnreadMessages();
  }

  initChat = (options: { userId?: string }): void => {
    const { userId } = options;
    if (!userId) {
      reportErrorSentry(new Error("Init chat without user ID"));
      return;
    }

    this.chatOpen = true;
    this.userId = userId;

    this.registerWebsocketObservers();
    this.addAppStateListeners();

    void this.getCachedMessages();

    appViewState.chatBloc = this;

    void pushNotificationState.removeAllDeliveredNotifications();
  };

  setChatOpen = (setTo: boolean): void => {
    this.chatOpen = setTo;

    // Reset response time toast flag when user exits chat (so it shows again next visit)
    if (!setTo) {
      this.responseTimeToastShown = false;
    }
  };

  log = (message: string, deets?: unknown) => {
    if (featureFlags.getFlag(FeatureFlagName.chatEvents)) {
      // eslint-disable-next-line no-console
      console.warn(`[CHAT] ${message}`, deets ?? undefined);
    }

    logSentryBreadcrumb("chat", message);
  };

  addAppStateListeners = (): void => {
    // Store the cleanup function for later disposal
    void App.addListener("appStateChange", (data) => {
      if (data.isActive) {
        void this.checkUnreadMessages();
      }
      if (this.chatOpen && data.isActive) {
        void this.getCachedMessages();
      }
    }).then((cleanup) => {
      this.appStateListenerCleanup = cleanup.remove;
    });
  };

  /**
   * Helper method for polling to decide whether to load first page or newer messages
   */
  private handlePollingLoadMessages = async (): Promise<void> => {
    if (this.allMessages.length === 0) {
      await this.loadFirstPageMessages();
    } else {
      await this.loadNewerMessages();
    }
  };

  checkUnreadMessages = async (): Promise<void> => {
    await this.pollingService.checkUnreadMessages(
      this.state.unreadMessages,
      (count) => {
        this.emit({ ...this.state, unreadMessages: count });
      }
    );
  };

  registerWebsocketObservers = (): void => {
    this.deps.websocket.addObserver(
      WebsocketMessageType.receivedMessage,
      this.websocketHandleMessage
    );
  };

  registerWebsocketObserversForNotification = (): void => {
    this.deps.websocket.addObserver(
      WebsocketMessageType.receivedMessage,
      this.websocketHandleMessageForNotification
    );
  };

  loadingStart = (): void => {
    this.emit({ ...this.state, loading: true });
  };

  loadingEnd = (): void => {
    this.emit({ ...this.state, loading: false });
  };

  websocketHandleMessage: ObserverCallback = (message): void => {
    if (!message) return;
    const { type, payload } = message;

    // Always process receivedMessage to update message state, even when chat is closed
    // This ensures local messages get replaced with server-confirmed messages
    if (type === WebsocketMessageType.receivedMessage) {
      // The payload from WebSocket has a nested structure: { payload: { ...actual message... } }
      const actualPayload = (
        payload as { payload: GetMessagesResponseItem | undefined } | undefined
      )?.payload;
      this.handleMessageSentSuccess(actualPayload);
      return;
    }

    // For other message types, only process when chat is open
    if (!this.chatOpen) return;
  };

  websocketHandleMessageForNotification: ObserverCallback = (message): void => {
    if (!message) return;
    const { type } = message;

    if (type === WebsocketMessageType.receivedMessage) {
      void this.checkUnreadMessages();
    }
  };

  handleMessageSentSuccess = (
    payload: GetMessagesResponseItem | undefined
  ): void => {
    if (!payload?.id) return;
    const message = ChatBloc.parseApiMessage(payload);
    this.allMessages.push(message);

    this.setMessagesToState();

    let newMessageNotificationType: "notification" | "scroll" = "notification";
    if (message.senderType === "USER" || this.scrollEndState) {
      newMessageNotificationType = "scroll";
    }

    const smoothScroll = message.senderType !== "USER" || this.scrollEndState;

    switch (newMessageNotificationType) {
      case "scroll":
        this.scrollToMessageId(message.id, smoothScroll);
        break;
      case "notification":
        this.setNewMessageNotification(true);
        break;
    }
  };

  getCachedMessages = async (): Promise<void> => {
    // Load local messages from storage into queue
    this.messageQueue.load();

    const cachedMessages = this.messageRepository.loadCached();

    if (cachedMessages.length > 0) {
      this.allMessages = cachedMessages
        .map(ChatBloc.parseApiMessage)
        .filter((msg) => !msg.computed.isLocal);

      this.setMessagesToState();
      await this.loadNewerMessages();
    } else {
      await this.loadFirstPageMessages(true);
    }

    await this.processLocalMessages();
  };

  backgroundUpdateMessages = async (): Promise<void> => {
    if (this.isClosed) return;
    try {
      await this.loadFirstPageMessages(false);
    } catch (e) {
      reportErrorSentry(e);
    }
  };

  cacheMessages = (messages: GetMessagesResponseItem[]): void => {
    this.messageRepository.cacheMessages(messages);
  };

  loadFirstPageMessages = async (showLoading = true): Promise<void> => {
    const messages = await this.messageRepository.loadFirstPage(
      showLoading,
      this.loadingStart,
      this.loadingEnd
    );

    this.allMessages.push(...messages.map(ChatBloc.parseApiMessage));
    this.setMessagesToState();
  };

  loadNewerMessages = async () => {
    const lastMessage = this.allMessages.findLast(
      (msg) => !msg.id.startsWith("local_")
    );
    if (!lastMessage) {
      reportErrorSentry(new Error("No messages to load"));
      return;
    }

    const messages = await this.messageRepository.loadNewer(lastMessage.id);
    this.allMessages.push(...messages.map(ChatBloc.parseApiMessage));
    this.setMessagesToState();
  };

  withoutLocalMessages = (messages: ChatMessage[]): ChatMessage[] => {
    return this.messageStateManager.withoutLocalMessages(messages);
  };

  firstMessageIndex = 0;
  lastMessageIndex = 0;
  setMessagesToState = (): void => {
    this.removeSentLocalMessages();

    // Count current state messages
    const currentCounts = this.messageStateManager.countMessages(
      this.state.messages
    );
    const localMessageCount = this.localMessagesParsed.length;

    // Merge and process messages using MessageStateManager
    const result = this.messageStateManager.mergeMessagesForState(
      this.allMessages,
      this.state.messages,
      this.localMessagesParsed,
      this.state.firstItemIndex
    );

    // Early return if no changes needed (optimization)
    if (
      !this.messageStateManager.shouldUpdateState(
        currentCounts,
        result.toCache.length,
        localMessageCount
      )
    ) {
      return;
    }

    // Emit updated state
    this.emit({
      ...this.state,
      messages: result.messages,
      firstItemIndex: result.firstItemIndex
    });

    // Cache remote messages
    this.cacheMessages(result.toCache);

    // Update global app view state
    appViewState.setMessagesState(result.messages);
  };

  handleMessageFile = async (
    file: File
  ): Promise<Record<string, string> | undefined> => {
    try {
      let metadata: Record<string, string> = {};
      const fileMeta = await FileMetadataUtils.prepareFileMetaData(file);
      const upload = await this.deps.files.uploadFile({ file });
      if (!upload) return;

      const fileKey = fileMeta.files;
      metadata = {
        ...fileMeta,
        [FileMetadataUtils.getFileMetaKey(fileKey, MessageMetaField.source)]:
          upload.id
      };

      return metadata;
    } catch (error) {
      reportErrorSentry(error);
      const errorMessage = (error as Error | undefined)?.message;
      if (errorMessage === appErrors.upload_max_filesize_exceeded) {
        this.deps.notifications.showError("error_file_too_large", {
          max: `${FileUploadMegabyteLimit}MB`,
          file: file.name
        });
      }
    }
  };

  postMessage = async (
    message: string,
    files: FileList | null,
    meta?: Record<string, string | string[] | boolean>
  ): Promise<void> => {
    let metadata: Record<string, string | string[] | boolean> = {};

    const filesArray = Array.from(files ?? []);

    try {
      const filesUploaded = await Promise.all([
        ...filesArray.map(async (file) => this.handleMessageFile(file))
      ]);
      const successFiles = filesUploaded.filter(Boolean);
      const allFilesFailed =
        Boolean(filesUploaded.length) && filesUploaded.every((file) => !file);

      // stop if all files failed
      if (allFilesFailed) return;

      const filesMetaKey = [];
      for (const fileMeta of successFiles) {
        filesMetaKey.push(fileMeta.files);
        metadata = {
          ...metadata,
          ...fileMeta,
          ...(meta ?? {})
        };
      }
      metadata.files = filesMetaKey.join(",");
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      return;
    }

    await this.sendMessageWithFallback({
      content: message,
      meta: metadata
    });

    // Show response time toast only on first message, after 300ms delay
    if (!this.responseTimeToastShown) {
      setTimeout(() => {
        if (!this.responseTimeToastShown && this.chatOpen) {
          this.deps.notifications.showResponseTimeToast();
          this.responseTimeToastShown = true;
        }
      }, 300);
    }
  };

  sendMessageWithFallback = async (msg: {
    content: string;
    meta?: Record<string, string | string[] | boolean>;
  }): Promise<void> => {
    await this.messageTransport.sendMessage({
      content: msg.content,
      userId: this.userId,
      meta: msg.meta,
      parseTextToHtml: this.parseTextToHtml,
      onLocalMessageCreated: this.addLocalMessage,
      onMessageSentSuccess: this.handleMessageSentSuccess,
      log: this.log,
      sendViaHTTP: this.sendMessageFallbackRequest
    });
  };

  get localMessages(): GetMessagesResponseItem[] {
    return this.messageQueue.getMessages();
  }

  set localMessages(messages: GetMessagesResponseItem[]) {
    this.messageQueue.setMessages(messages);
  }

  sendMessageFallbackRequest = async (msg: {
    contentType: string;
    contentValue?: string;
    metadata?: AnyObject;
  }): Promise<void> => {
    await this.messageTransport.sendMessageViaHTTP({
      payload: msg,
      userId: this.userId,
      onMessageSentSuccess: this.handleMessageSentSuccess,
      log: this.log
    });
  };

  addLocalMessage = (message: GetMessagesResponseItem): void => {
    this.messageQueue.addMessage(message);
    this.setMessagesToState();
  };

  get localMessagesParsed(): ChatMessage[] {
    return this.localMessages.map(ChatBloc.parseApiMessage);
  }

  // Retry helper methods - delegate to MessageQueue
  private getRetryCount(message: GetMessagesResponseItem): number {
    return this.messageQueue.getRetryCount(message);
  }

  hasExceededRetries(message: GetMessagesResponseItem): boolean {
    return this.messageQueue.hasExceededRetries(message);
  }

  private calculateNextRetryDelay(message: GetMessagesResponseItem): number {
    return this.messageQueue.calculateNextRetryDelay(message);
  }

  public isReadyForRetry(message: GetMessagesResponseItem): boolean {
    return this.messageQueue.isReadyForRetry(message);
  }

  processLocalMessages = async (): Promise<void> => {
    // Check if already processing (prevent concurrent execution)
    if (!this.messageQueue.acquireLock()) {
      this.log("processLocalMessages already running, queueing request");

      // Return a promise that resolves when processing is complete
      return new Promise<void>((resolve) => {
        this.messageQueue.addPendingRequest(resolve);
      });
    }

    try {
      this.removeSentLocalMessages();

      const { localMessages } = this;
      if (localMessages.length === 0) return;

      for (const message of localMessages) {
        // Extract localId to check if message is already being sent
        const localMeta = message.metadata ?? {};
        const localId = String(localMeta[MESSAGE_METADATA_KEYS.LOCAL_ID]);

        // Check message age (24 hour expiration)
        if (this.messageQueue.isExpired(message)) {
          const messageAge =
            Date.now() -
            new Date(
              String(
                message.metadata?.[MESSAGE_METADATA_KEYS.SEND_TIMESTAMP] ?? ""
              )
            ).getTime();
          const oneHour = 60 * 60 * 1000;
          this.log(
            "Message expired (older than 24 hours), moving to dead letter queue",
            {
              localId,
              age: messageAge,
              ageHours: Math.round(messageAge / oneHour)
            }
          );

          // Move to dead letter queue with expiration metadata
          const expiredMessage = {
            ...message,
            metadata: {
              ...message.metadata,
              [MESSAGE_METADATA_KEYS.EXPIRED]: true,
              [MESSAGE_METADATA_KEYS.EXPIRED_AT]: Date.now(),
              [MESSAGE_METADATA_KEYS.AGE_HOURS]: Math.round(
                messageAge / oneHour
              )
            }
          };
          this.messageQueue.moveToDeadLetterQueue(expiredMessage);
          this.messageQueue.removeMessage(localId);

          continue;
        }

        // Check if message has exceeded retry limit (max 3 retries)
        if (this.hasExceededRetries(message)) {
          this.log(
            "Message exceeded retry limit, moving to dead letter queue",
            {
              localId,
              retryCount: this.getRetryCount(message)
            }
          );

          this.messageQueue.moveToDeadLetterQueue(message);
          this.messageQueue.removeMessage(localId);

          continue;
        }

        // Check if message is ready for retry (respects exponential backoff)
        if (!this.isReadyForRetry(message)) {
          this.log("Message not ready for retry, skipping (backoff delay)", {
            localId,
            retryCount: this.getRetryCount(message),
            nextRetryIn:
              this.calculateNextRetryDelay(message) -
              (Date.now() -
                ((message.metadata?.[
                  MESSAGE_METADATA_KEYS.LAST_RETRY_TIME
                ] as number) || Date.now()))
          });
          continue;
        }

        // Skip if message is already being sent via HTTP
        if (this.messageTransport.isSending(localId)) {
          this.log(
            "Message already being sent, skipping in processLocalMessages",
            {
              localId
            }
          );
          continue;
        }

        const payload = {
          contentType: message.contentType,
          contentValue: message.contentValue,
          metadata: message.metadata
        };

        const wsMessage = {
          type: WebsocketMessageType.sendMessage,
          payload
        };
        try {
          if (this.deps.websocket.getConnectionState() === WS_STATE.OPEN) {
            // Use MessageTransport to track WebSocket sends and prevent duplicates
            await this.messageTransport.sendViaWebSocket(
              wsMessage,
              localId,
              this.log
            );
          } else {
            this.log("WS is disconnected, posting message");
            await this.sendMessageFallbackRequest(payload);
          }
        } catch (e) {
          // Increment retry count on failure
          this.log("Message send failed, incrementing retry count", {
            localId,
            retryCount: this.getRetryCount(message)
          });

          // Update message with incremented retry count
          const updatedMessage = this.messageQueue.incrementRetry(message);
          this.messageQueue.updateMessage(localId, updatedMessage);

          reportErrorSentry(
            new Error("Sending Message WS Error", { cause: e })
          );
        }
      }

      this.removeSentLocalMessages();
    } finally {
      // Release lock and resolve pending requests
      this.messageQueue.releaseLock();
    }
  };

  removeSentLocalMessages = (): void => {
    this.messageQueue.removeSentMessages(this.allMessages);
  };

  // Dead letter queue constants moved to ChatConstants
  static deadLetterQueueLimit = DEAD_LETTER_QUEUE.MAX_MESSAGES;
  static deadLetterKey = DEAD_LETTER_QUEUE.STORAGE_KEY;

  getDeadLetterMessages(): GetMessagesResponseItem[] {
    return this.messageQueue.getDeadLetterMessages();
  }

  public readonly parseTextToHtml = (text: string): string => {
    const content = text.trim().replace(/\n/g, "<br>").replace(/\s/g, "&nbsp;");
    return `<div>${content}</div>`;
  };

  /**
   * Load the next page of messages
   */
  public readonly loadPreviousMessages = async (): Promise<void> => {
    if (!this.messageRepository.hasMoreHistory() || this.state.loading) {
      return;
    }
    const [firstMessage] = this.state.messages;

    try {
      this.emit({ ...this.state, loading: true });
      const messages = await this.messageRepository.loadOlder(firstMessage.id);
      const newMessages = messages.map(ChatBloc.parseApiMessage);

      if (newMessages.length > 0) {
        this.allMessages.push(...newMessages);
        this.setMessagesToState();
      }
    } catch (e) {
      reportErrorSentry(e);
    } finally {
      this.emit({ ...this.state, loading: false });
    }
  };

  public readonly setNewMessageNotification = (set: boolean): void => {
    this.emit({
      ...this.state,
      newMessageAvailable: set
    });
  };

  public readonly registerVirtualList = (
    element: VirtuosoHandle | null
  ): void => {
    // eslint-disable-next-line no-console
    if (this.verbose) console.log("registerVirtualList", element);
    this.virtualListElement = element;
  };

  scrollToMessageId = (id: string, smooth = true): void => {
    // add timeout to allow state to be updated before we scroll
    setTimeout(() => {
      requestAnimationFrame(() => {
        if (!this.virtualListElement) {
          // eslint-disable-next-line no-console
          console.warn("virtualListElement not found");
          return;
        }

        const index = this.state.messages.findIndex((m) => m.id === id);
        if (index === -1) {
          reportErrorSentry(new Error("Message not found"));
          return;
        }

        this.virtualListElement.scrollToIndex({
          index,
          behavior: smooth ? "smooth" : "auto",
          align: "start"
        });
      });
    }, TIMEOUTS.SCROLL_DELAY_MS);
  };

  scrollToBottom = (smooth: boolean): void => {
    // add timeout to allow state to be updated before we scroll
    setTimeout(() => {
      requestAnimationFrame(() => {
        if (!this.virtualListElement) {
          // eslint-disable-next-line no-console
          console.warn("virtualListElement not found");
          return;
        }

        this.scrollEndState = true;
        this.virtualListElement.scrollToIndex({
          index: this.state.messages.length + VIRTUAL_SCROLL.SCROLL_BUFFER,
          behavior: smooth ? "smooth" : "auto",
          align: "end"
        });
      });
    }, TIMEOUTS.SCROLL_DELAY_MS);
  };

  setScrollEndState = (setTo: boolean): void => {
    this.scrollEndState = setTo;
    this.setNewMessageNotification(false);
  };

  addError = (
    message: ChatErrorMessages,
    blocking = false,
    text?: string
  ): void => {
    reportErrorSentry(new Error(message));

    this.emit({
      ...this.state,
      errors: [
        ...this.state.errors,
        {
          message,
          text,
          blocking
        }
      ]
    });

    // remove the error after timeout if it's not blocking
    if (!blocking) {
      setTimeout(() => {
        this.emit({
          ...this.state,
          errors: this.state.errors.filter((e) => e.message !== message)
        });
      }, TIMEOUTS.ERROR_AUTO_DISMISS_MS);
    }
  };

  /*
  STATIC METHODS
  */

  public static readonly parseApiMessage = (
    apiMessage: GetMessagesResponseItem
  ): ChatMessage => {
    let text = apiMessage.contentValue ?? "";
    if (text === "<div></div>") {
      text = "";
    }

    const meta =
      apiMessage.metadata ??
      ({} as Record<string, boolean | number | string | undefined>);
    const smd =
      apiMessage.senderMetadata ??
      ({} as Record<string, boolean | number | string | undefined>);
    const metaType = meta["message:type"] as ChatMessageMetaType | undefined;
    const contentValueGenerated = meta["content-value:generated"] as
      | boolean
      | undefined;
    const filesIdList = meta.filesIdList as string[] | undefined;

    const dateLocal = parseAbsoluteToLocal(apiMessage.messageReceiveTimestamp);

    const isLocal = apiMessage.id.startsWith("local_");

    const localRefId: string | undefined = meta[
      MESSAGE_METADATA_KEYS.LOCAL_ID
    ] as string | undefined;

    const dateTimeFormat = new DateFormatter(getSupportedUserLocale(), {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hourCycle: "h12"
    });

    const computed: ChatMessageComputedData = {
      localRefId,
      isLocal,
      sentByUser:
        smd.role === "USER" &&
        apiMessage.senderId === apiMessage.messageOwnerId,
      fileAttachments: FileMetadataUtils.getMessageFileAttachments(apiMessage),
      attachedLifelineItems:
        ChatBloc.getMessageAttachedLifelineItems(apiMessage),
      senderDescription: ChatBloc.getMessageSenderDescription(apiMessage),
      senderName: `${smd.displayName ?? ""}`.trim(),
      messageSentDate: dateTimeFormat.format(dateLocal.toDate()),
      isLabValueDisplay: metaType === ChatMessageMetaType.labValueDisplay,
      isLabReportUpload: metaType === ChatMessageMetaType.labReportUpload,
      isNpsFeedback: metaType === ChatMessageMetaType.npsFeedback,
      contentValueGenerated,
      filesIdList
    };

    computed.showContentValue = !contentValueGenerated;

    const msg: ChatMessage = {
      id: apiMessage.id,
      apiItem: apiMessage,
      computed,
      text,
      type: apiMessage.contentType as ChatMessageType,
      timestamp: apiMessage.messageReceiveTimestamp,
      senderType: smd.role as KnownSenderType,
      metaType,
      contentValueGenerated
    };
    return msg;
  };

  public static readonly getMessageAttachedLifelineItems = (
    message: GetMessagesResponseItem
  ): string[] => {
    const meta = message.metadata ?? {};
    return (
      (meta["message:lifeline-item-ids"] as unknown as string[] | undefined) ??
      []
    );
  };

  public getMessagesThatContainMetadataAttribute = (
    metadataKey: string,
    metadataValue: unknown,
    sortByDate?: boolean
  ): ChatMessage[] => {
    const filteredMessages = this.allMessages.filter(
      (message) => message.apiItem?.metadata?.[metadataKey] === metadataValue
    );

    if (sortByDate) {
      filteredMessages.sort((a, b) => {
        const aDate = new Date(a.timestamp);
        const bDate = new Date(b.timestamp);
        return aDate.getTime() - bDate.getTime();
      });
    }

    return filteredMessages;
  };

  public static readonly getMessageSenderDescription = (
    message: GetMessagesResponseItem
  ): string => {
    const smd: Record<string, string | undefined> =
      message.senderMetadata ?? {};
    const jobTitleTranslation = translate(
      `job_title_${smd.role}` as TranslationKey,
      {},
      "" as TranslationKey
    );
    const jobTitle = smd.displayRole ?? jobTitleTranslation;
    return jobTitle;
  };

  lastReadMessageId: string | null = null;
  markMessageAsRead = (message: ChatMessage): void => {
    if (this.lastReadMessageId === message.id) return;
    MessageControllerService.markMessageAsRead(message.id)
      .then(() => {
        this.lastReadMessageId = message.id;
        this.emit({
          ...this.state,
          unreadMessages: 0
        });
      })
      .catch(() => {
        // handled by the API controller
      });
  };

  /**
   * Cleanup method to prevent memory leaks
   * Removes all event listeners and stops all services
   * Call this when the ChatBloc instance is no longer needed
   */
  dispose = async (): Promise<void> => {
    this.log("Disposing ChatBloc instance");

    // Mark as closed to prevent further operations
    this.isClosed = true;

    // Stop polling service
    this.pollingService.stop();

    // Clear message transport state
    this.messageTransport.clearSendingState();

    // Clear message queue
    this.messageQueue.clear();
    this.messageQueue.clearProcessingState();

    // Remove window event listener
    window.removeEventListener(globalEvents.USER_CLEAR, this.userClearHandler);

    // Remove app state listener
    if (this.appStateListenerCleanup) {
      await this.appStateListenerCleanup();
    }

    // Note: WebSocket observers are managed by the WebSocketBloc
    // and will be cleaned up when the WebSocket connection is closed
    // If needed in the future, we could track and remove them here

    this.log("ChatBloc disposed successfully");
  };
}
